import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import pickleballIcon from "./pickleball.png";
import AddressSearchModal from "./components/AddressSearchModal";
import { compressImage } from "./utils/imageUtils";


const SPORT_LEVELS = {
  tennis:      { icon: "🎾", label: "테니스",       levelLabel: "NTRP 지수",         levels: ["2.0","2.5","3.0","3.5","4.0","4.5","5.0+"] },
  soccer:      { icon: "⚽", label: "축구",         levelLabel: "티어",              levels: ["브론즈","실버","골드","플래티넘","다이아"] },
  futsal:      { icon: "🥅", label: "풋살",         levelLabel: "티어",              levels: ["브론즈","실버","골드","플래티넘","다이아"] },
  badminton:   { icon: "🏸", label: "배드민턴",     levelLabel: "전국 급수",          levels: ["초심","D조","C조","B조","A조"] },
  basketball:  { icon: "🏀", label: "농구",         levelLabel: "숙련도",            levels: ["하 (입문)","중 (경험자)","상 (숙련)"] },
  bowling:     { icon: "🎳", label: "볼링",         levelLabel: "공인 에버리지(AVG)", levels: ["~100","100~130","130~160","160~180","180~200","200+"] },
  tabletennis: { icon: "🏓", label: "탁구",         levelLabel: "부수",              levels: ["희망부","6부","5부","4부","3부","2부","1부"] },
  baseball:    { icon: "⚾", label: "야구",         levelLabel: "숙련도",            levels: ["하 (입문)","중 (경험자)","상 (숙련)"] },
  golf:        { icon: "⛳", label: "골프",         levelLabel: "핸디캡",            levels: ["보기 플레이어","90대","80대","70대 이하"] },
  running:     { icon: null, label: "피클볼",       levelLabel: "수준",              levels: ["입문","초급","중급","고급"] },
  billiards:   { icon: "🎱", label: "당구",         levelLabel: "급수",              levels: ["초심","200 이하","200~300","300~400","400+"] },
};

const AGE_GROUPS = ["10대","20대","30대","40대","50대 이상","60대+"];

const CAREER_STEPS = [
  { label: "입문",   desc: "이제 막 시작한 팀" },
  { label: "6개월",  desc: "기초를 익히는 단계" },
  { label: "1년",    desc: "기본기가 갖춰진 팀" },
  { label: "2년",    desc: "초급~중급 수준의 팀" },
  { label: "3년",    desc: "중급 수준의 팀" },
  { label: "5년",    desc: "중급~고급 수준의 팀" },
  { label: "7년",    desc: "경험 많은 베테랑 팀" },
  { label: "10년+",  desc: "준 선수급 실력의 팀" },
];

const SEOUL_DISTRICTS = [
  "종로구","중구","용산구","성동구","광진구","동대문구","중랑구","성북구",
  "강북구","도봉구","노원구","은평구","서대문구","마포구","양천구","강서구",
  "구로구","금천구","영등포구","동작구","관악구","서초구","강남구","송파구","강동구",
];

const SKILL_LEVELS_GEN = ["입문","초급","중급","고급"];
const DAYS_OF_WEEK     = ["월","화","수","목","금","토","일"];
const TIMES_OF_DAY     = ["오전","오후","저녁"];


function parseAgeGroup(ag) {
  if (!ag) return [];
  if (Array.isArray(ag)) return ag;
  try { const p = JSON.parse(ag); return Array.isArray(p) ? p : []; } catch { return []; }
}

function parseArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
}

function careerIndexFromLabel(label) {
  const idx = CAREER_STEPS.findIndex((c) => c.label === label);
  return idx >= 0 ? idx : 3;
}

export default function TeamRegister({ editTeam = null, onBack, onSaved }) {
  const { user } = useAuth();
  const isEdit = !!editTeam;

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    teamName: "", sport: "", level: "", intro: "",
    region: "", mainPlace: "", mainPlaceAddress: "", mainPlaceMapx: null, mainPlaceMapy: null,
    skillLevel: "",
    maleCount: "", femaleCount: "", ages: [], career: 3,
    availableDays: [], availableTimes: [],
    parking: null, parkingCount: "",
  });
  const [errors, setErrors]       = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showAddrModal, setShowAddrModal] = useState(false);

  // 팀 이름 중복 확인
  const [nameStatus, setNameStatus] = useState(""); // "" | "checking" | "available" | "duplicate"
  const nameTimerRef = useRef(null);

  // 이미지 상태
  const [logoFile,        setLogoFile]        = useState(null);
  const [photoFile,       setPhotoFile]       = useState(null);
  const [logoPreview,     setLogoPreview]     = useState(editTeam?.logo_url   || "");
  const [photoPreview,    setPhotoPreview]    = useState(editTeam?.team_photo || "");
  const [logoCompressing, setLogoCompressing] = useState(false);
  const [photoCompressing, setPhotoCompressing] = useState(false);
  const logoInputRef  = useRef(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (editTeam) {
      setForm({
        teamName:         editTeam.team_name || "",
        sport:            editTeam.sports_type || "",
        level:            editTeam.level || "",
        intro:            editTeam.intro || "",
        region:           editTeam.region || "",
        mainPlace:        editTeam.main_place || "",
        mainPlaceAddress: editTeam.main_place_address || "",
        mainPlaceMapx:    editTeam.main_place_mapx || null,
        mainPlaceMapy:    editTeam.main_place_mapy || null,
        skillLevel:       editTeam.skill_level || "",
        maleCount:        editTeam.male_count   != null ? String(editTeam.male_count)   : "",
        femaleCount:      editTeam.female_count != null ? String(editTeam.female_count) : "",
        ages:             parseAgeGroup(editTeam.age_group),
        career:           careerIndexFromLabel(editTeam.member_avg_career),
        availableDays:    parseArray(editTeam.available_days),
        availableTimes:   parseArray(editTeam.available_times),
        parking:          editTeam.parking != null ? editTeam.parking : null,
        parkingCount:     editTeam.parking_count != null ? String(editTeam.parking_count) : "",
      });
      // 수정 시 기존 이미지 미리보기 복원
      setLogoPreview(editTeam.logo_url || "");
      setPhotoPreview(editTeam.team_photo || "");
      setLogoFile(null);
      setPhotoFile(null);
    }
  }, [editTeam]);

  // 이미지 선택 핸들러 (자동 WebP 압축)
  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert("팀 로고는 10MB 이하만 업로드할 수 있어요"); return; }
    setLogoCompressing(true);
    const compressed = await compressImage(file, 400, 400, 0.85);
    setLogoFile(compressed);
    setLogoPreview(URL.createObjectURL(compressed));
    setLogoCompressing(false);
  };
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { alert("단체사진은 20MB 이하만 업로드할 수 있어요"); return; }
    setPhotoCompressing(true);
    const compressed = await compressImage(file, 1200, 1200, 0.85);
    setPhotoFile(compressed);
    setPhotoPreview(URL.createObjectURL(compressed));
    setPhotoCompressing(false);
  };

  // Supabase Storage 업로드 (압축 후 webp로 변환된 파일을 받아서 저장)
  // 로고: bucket="team-logos" / 단체사진: bucket="team-images"
  const uploadImage = async (file, teamId, type, bucket) => {
    const ext      = file.type === "image/webp" ? "webp" : file.name.split(".").pop().toLowerCase();
    const fileName = `${teamId}/${type}_${Date.now()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage.from(bucket).getPublicUrl(fileName);
    return data.publicUrl;
  };

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  // 팀 이름 변경 시 debounce 중복 확인
  const handleTeamNameChange = useCallback((value) => {
    update("teamName", value);
    setNameStatus("");
    if (nameTimerRef.current) clearTimeout(nameTimerRef.current);
    const trimmed = value.trim();
    if (!trimmed || (isEdit && trimmed === editTeam?.team_name)) return;
    nameTimerRef.current = setTimeout(async () => {
      setNameStatus("checking");
      const { data } = await supabase
        .from("teams")
        .select("id")
        .eq("team_name", trimmed)
        .limit(1);
      if (data && data.length > 0) {
        // 수정 모드에서 자기 팀은 허용
        if (isEdit && data[0].id === editTeam?.id) setNameStatus("available");
        else setNameStatus("duplicate");
      } else {
        setNameStatus("available");
      }
    }, 500);
  }, [isEdit, editTeam]);

  const toggleAge  = (v) => setForm((p) => ({ ...p, ages:          p.ages.includes(v)          ? p.ages.filter((x) => x !== v)          : [...p.ages, v] }));
  const toggleDay  = (v) => setForm((p) => ({ ...p, availableDays:  p.availableDays.includes(v)  ? p.availableDays.filter((x) => x !== v)  : [...p.availableDays, v] }));
  const toggleTime = (v) => setForm((p) => ({ ...p, availableTimes: p.availableTimes.includes(v) ? p.availableTimes.filter((x) => x !== v) : [...p.availableTimes, v] }));

  const validateStep1 = () => {
    const e = {};
    if (!form.teamName.trim()) e.teamName   = "팀명을 입력해주세요";
    else if (nameStatus === "duplicate") e.teamName = "이미 사용 중인 팀 이름입니다";
    if (!form.sport)           e.sport      = "종목을 선택해주세요";
    if (!form.level)           e.level      = "팀 레벨을 선택해주세요";
    if (!form.region)          e.region     = "활동 지역을 선택해주세요";
    if (!form.skillLevel)      e.skillLevel = "실력 레벨을 선택해주세요";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.maleCount && !form.femaleCount) e.gender = "인원수를 입력해주세요";
    if (form.ages.length === 0)               e.ages   = "연령대를 선택해주세요";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const saveToSupabase = async () => {
    setIsLoading(true);
    setSaveError("");
    const male   = parseInt(form.maleCount)  || 0;
    const female = parseInt(form.femaleCount) || 0;
    const payload = {
      team_name:           form.teamName.trim(),
      sports_type:         form.sport,
      level:               form.level,
      male_count:          male,
      female_count:        female,
      gender_ratio:        `남 ${male}명 / 여 ${female}명`,
      age_group:           form.ages,
      member_avg_career:   CAREER_STEPS[form.career].label,
      intro:               form.intro.trim() || null,
      region:              form.region || null,
      main_place:          form.mainPlace || null,
      main_place_address:  form.mainPlaceAddress || null,
      main_place_mapx:     form.mainPlaceMapx || null,
      main_place_mapy:     form.mainPlaceMapy || null,
      skill_level:         form.skillLevel || null,
      available_days:      form.availableDays.length  > 0 ? form.availableDays  : null,
      available_times:     form.availableTimes.length > 0 ? form.availableTimes : null,
      parking:             form.parking,
      parking_count:       form.parking === true && form.parkingCount ? parseInt(form.parkingCount) : null,
    };

    let teamId = editTeam?.id;
    let error;
    if (isEdit) {
      ({ error } = await supabase.from("teams").update(payload).eq("id", teamId));
    } else {
      const { data: newTeam, error: insertErr } = await supabase
        .from("teams").insert([{ ...payload, user_id: user?.id }]).select().single();
      error = insertErr;
      if (newTeam) teamId = newTeam.id;
    }

    if (error) { setIsLoading(false); setSaveError("저장 중 오류가 발생했어요: " + error.message); return false; }

    // 새 팀 생성 시 team_members에 팀장으로 자동 등록
    if (!isEdit && teamId && user?.id) {
      await supabase.from("team_members").insert({
        team_id: teamId,
        user_id: user.id,
        role: "leader",
        status: "accepted",
      });
    }

    // 이미지 업로드 및 DB 저장 (팀 ID 확정 후 실행)
    if (teamId) {
      const imgUpdate = {};
      try {
        if (logoFile) {
          imgUpdate.logo_url = await uploadImage(logoFile, teamId, "logo", "team-logos");
        }
        if (photoFile) {
          imgUpdate.team_photo = await uploadImage(photoFile, teamId, "photo", "team-images");
        }
        // 수정 시 이미지를 삭제한 경우 DB도 null로 반영
        if (isEdit) {
          if (!logoFile && !logoPreview && editTeam?.logo_url)     imgUpdate.logo_url   = null;
          if (!photoFile && !photoPreview && editTeam?.team_photo) imgUpdate.team_photo = null;
        }

        if (Object.keys(imgUpdate).length > 0) {
          const { error: imgErr } = await supabase
            .from("teams")
            .update(imgUpdate)
            .eq("id", teamId);
          if (imgErr) setSaveError("이미지 URL 저장 실패: " + imgErr.message);
        }
      } catch (err) {
        setSaveError("이미지 업로드 중 오류가 발생했어요: " + err.message);
        setIsLoading(false);
        return false;
      }
    }

    setIsLoading(false);
    return true;
  };

  const handleNext = async () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) {
      const ok = await saveToSupabase();
      if (ok) setStep(3);
    }
  };

  const selectedSport = SPORT_LEVELS[form.sport];
  const careerInfo    = CAREER_STEPS[form.career];
  const totalMembers  = (parseInt(form.maleCount) || 0) + (parseInt(form.femaleCount) || 0);

  // ── 완료 화면 ──────────────────────────────────────────────────────────
  if (step === 3) {
    return (
      <div style={styles.container}>
        <div style={styles.successWrapper}>
          <div style={styles.successBurst} />
          <div style={styles.successIconWrap}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
              stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <p style={styles.successTitle}>{isEdit ? "수정 완료!" : "등록 완료!"}</p>
          <p style={styles.successTeam}>{form.teamName}</p>
          <p style={styles.successSub}>팀 정보가 성공적으로 저장됐어요 ✅</p>

          <div style={styles.summaryCard}>
            <SummaryRow icon="🏅" label="종목"    value={`${selectedSport?.icon} ${selectedSport?.label}`} />
            <SummaryRow icon="📊" label="레벨"    value={form.level} />
            <SummaryRow icon="📍" label="지역"    value={form.region || "-"} />
            <SummaryRow icon="⚡" label="실력"    value={form.skillLevel || "-"} />
            <SummaryRow icon="👥" label="인원"    value={`총 ${totalMembers}명 (남 ${form.maleCount || 0} / 여 ${form.femaleCount || 0})`} />
            <SummaryRow icon="🎂" label="연령대"  value={form.ages.join(", ")} />
            <SummaryRow icon="⏱" label="평균구력" value={careerInfo.label} last />
          </div>

          {onSaved ? (
            <button style={styles.primaryBtn} onClick={onSaved}>팀 관리로 돌아가기</button>
          ) : (
            <button style={styles.primaryBtn} onClick={() => {
              setStep(1);
              setForm({ teamName:"", sport:"", level:"", intro:"", region:"", mainPlace:"", mainPlaceAddress:"", mainPlaceMapx:null, mainPlaceMapy:null, skillLevel:"", maleCount:"", femaleCount:"", ages:[], career:3, availableDays:[], availableTimes:[], parking:null, parkingCount:"" });
              setSaveError("");
            }}>새 팀 등록하기</button>
          )}
        </div>
      </div>
    );
  }

  // ── 등록/수정 폼 ────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          {onBack && (
            <button style={styles.backBtn} onClick={onBack}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="#E24B4A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          <div style={styles.logoRow}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={styles.logoText}>크루픽</span>
              <span style={styles.logoSub}>title</span>
            </div>
          </div>
        </div>
        <div style={styles.headerTitleRow}>
          <div style={styles.headerTitle}>{isEdit ? "팀 정보 수정" : "새 팀 등록"}</div>
          <div style={styles.stepRow}>
            {[1, 2].map((s) => (
              <div key={s} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <div style={{ ...styles.stepDot, background: step >= s ? "#E24B4A" : "#e5e7eb", color: step >= s ? "#fff" : "#9ca3af" }}>{s}</div>
                <span style={{ fontSize: 11, color: step >= s ? "#E24B4A" : "#9ca3af" }}>
                  {s === 1 ? "팀 정보" : "구성원"}
                </span>
                {s < 2 && <div style={styles.stepLine} />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={styles.body}>
        {step === 1 ? (
          <>
            {/* 팀명 */}
            <FieldGroup label="팀명" required error={errors.teamName}>
              <input
                style={{ ...styles.textInput, borderColor: nameStatus === "duplicate" || errors.teamName ? "#E24B4A" : nameStatus === "available" ? "#16a34a" : "#e5e7eb" }}
                placeholder="예: 마포 FC, 강남 테니스 클럽"
                value={form.teamName}
                onChange={(e) => handleTeamNameChange(e.target.value)}
              />
              {nameStatus === "checking" && <p style={{ fontSize: 12, color: "#9ca3af", margin: "4px 0 0" }}>중복 확인 중...</p>}
              {nameStatus === "duplicate" && <p style={{ fontSize: 12, color: "#dc2626", margin: "4px 0 0", fontWeight: 600 }}>이미 사용 중인 팀 이름입니다</p>}
              {nameStatus === "available" && <p style={{ fontSize: 12, color: "#16a34a", margin: "4px 0 0", fontWeight: 600 }}>사용 가능한 팀 이름입니다</p>}
            </FieldGroup>

            {/* 종목 */}
            <FieldGroup label="종목" required error={errors.sport}>
              <div style={styles.sportGrid}>
                {Object.entries(SPORT_LEVELS).map(([key, sp]) => (
                  <button key={key}
                    style={{ ...styles.sportBtn, borderColor: form.sport === key ? "#E24B4A" : "#e5e7eb", background: form.sport === key ? "#fff5f5" : "#f5f6fa" }}
                    onClick={() => { update("sport", key); update("level", ""); }}
                  >
                    <span style={{ fontSize: 22 }}>{sp.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 22, height: 22, objectFit: "contain" }} />}</span>
                    <span style={{ fontSize: 12, color: form.sport === key ? "#E24B4A" : "#6b7280", marginTop: 4 }}>{sp.label}</span>
                  </button>
                ))}
              </div>
            </FieldGroup>

            {/* 종목별 레벨 */}
            {form.sport && (
              <FieldGroup label={selectedSport.levelLabel} required error={errors.level}>
                <div style={styles.chipWrap}>
                  {selectedSport.levels.map((lv) => (
                    <button key={lv}
                      style={{ ...styles.chip, borderColor: form.level === lv ? "#E24B4A" : "#e5e7eb", background: form.level === lv ? "#E24B4A" : "#f5f6fa", color: form.level === lv ? "#fff" : "#6b7280" }}
                      onClick={() => update("level", lv)}
                    >{lv}</button>
                  ))}
                </div>
              </FieldGroup>
            )}

            {/* 실력 레벨 (일반) */}
            <FieldGroup label="실력 레벨" required error={errors.skillLevel} hint="전체 수준 기준">
              <div style={styles.chipWrap}>
                {SKILL_LEVELS_GEN.map((lv) => (
                  <button key={lv}
                    style={{ ...styles.chip, borderColor: form.skillLevel === lv ? "#E24B4A" : "#e5e7eb", background: form.skillLevel === lv ? "#E24B4A" : "#f5f6fa", color: form.skillLevel === lv ? "#fff" : "#6b7280" }}
                    onClick={() => update("skillLevel", lv)}
                  >{lv}</button>
                ))}
              </div>
            </FieldGroup>

            {/* 활동 지역 */}
            <FieldGroup label="활동 지역" required error={errors.region} hint="서울 기준">
              <select
                style={{ ...styles.textInput, color: form.region ? "#111827" : "#9ca3af" }}
                value={form.region}
                onChange={(e) => update("region", e.target.value)}
              >
                <option value="">구 선택...</option>
                {SEOUL_DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </FieldGroup>

            {/* 주 활동 장소 */}
            <FieldGroup label="주 활동 장소" hint="선택">
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  style={{ ...styles.textInput, flex: 1 }}
                  placeholder="예: 망원 풋살장, 잠실 테니스장"
                  value={form.mainPlace}
                  onChange={(e) => update("mainPlace", e.target.value)}
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => setShowAddrModal(true)}
                  style={{ padding: "0 16px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  🔍 검색
                </button>
              </div>
              {form.mainPlaceAddress && (
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>📍 {form.mainPlaceAddress}</div>
              )}
            </FieldGroup>

            {/* 팀 소개 */}
            <FieldGroup label="팀 소개" hint="선택">
              <textarea
                style={styles.textarea}
                placeholder="우리 팀을 소개해주세요 (최대 200자)"
                maxLength={200}
                value={form.intro}
                onChange={(e) => update("intro", e.target.value)}
                rows={3}
              />
            </FieldGroup>

            {/* 팀 로고 */}
            <FieldGroup label="팀 로고" hint="선택 · 정사각형 · 최대 10MB (자동 압축)">
              <input ref={logoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogoChange} />
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div
                  style={styles.logoUploadBox}
                  onClick={() => !logoCompressing && logoInputRef.current?.click()}
                >
                  {logoCompressing ? (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 20, marginBottom: 4 }}>⚙️</div>
                      <div style={{ fontSize: 10, color: "#6b7280" }}>이미지 최적화 중...</div>
                    </div>
                  ) : logoPreview ? (
                    <img src={logoPreview} alt="팀 로고" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 12 }} />
                  ) : (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 28, marginBottom: 4 }}>📷</div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>로고 업로드</div>
                    </div>
                  )}
                </div>
                <div>
                  <button style={{ ...styles.uploadBtn, opacity: logoCompressing ? 0.5 : 1 }} disabled={logoCompressing} onClick={() => logoInputRef.current?.click()}>
                    {logoCompressing ? "최적화 중..." : logoPreview ? "변경하기" : "사진 선택"}
                  </button>
                  {logoPreview && !logoCompressing && (
                    <button style={{ ...styles.uploadBtn, marginTop: 6, color: "#9ca3af", background: "#f5f6fa", border: "1px solid #e5e7eb" }}
                      onClick={() => { setLogoFile(null); setLogoPreview(""); }}
                    >삭제</button>
                  )}
                </div>
              </div>
            </FieldGroup>

            {/* 단체사진 */}
            <FieldGroup label="팀 단체사진" hint="선택 · 가로형 권장 · 최대 20MB (자동 압축)">
              <input ref={photoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhotoChange} />
              {photoCompressing ? (
                <div style={{ ...styles.photoUploadBox, cursor: "default" }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>⚙️</div>
                  <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 600 }}>이미지 최적화 중...</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>잠시만 기다려주세요</div>
                </div>
              ) : photoPreview ? (
                <div style={{ position: "relative" }}>
                  <img src={photoPreview} alt="단체사진" style={styles.photoPreview} />
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button style={{ ...styles.uploadBtn, flex: 1 }} onClick={() => photoInputRef.current?.click()}>변경하기</button>
                    <button style={{ ...styles.uploadBtn, flex: 1, color: "#9ca3af", background: "#f5f6fa", border: "1px solid #e5e7eb" }}
                      onClick={() => { setPhotoFile(null); setPhotoPreview(""); }}
                    >삭제</button>
                  </div>
                </div>
              ) : (
                <div style={styles.photoUploadBox} onClick={() => photoInputRef.current?.click()}>
                  <div style={{ fontSize: 32, marginBottom: 6 }}>🖼️</div>
                  <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 600 }}>단체사진 업로드</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>탭하여 사진 선택</div>
                </div>
              )}
            </FieldGroup>
          </>
        ) : (
          <>
            {/* 성별 인원수 */}
            <FieldGroup label="성별 인원수" required error={errors.gender}>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={styles.genderLabel}>남성</div>
                  <input style={{ ...styles.textInput, textAlign: "center" }}
                    type="number" placeholder="0" min="0"
                    value={form.maleCount}
                    onChange={(e) => update("maleCount", e.target.value)}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={styles.genderLabel}>여성</div>
                  <input style={{ ...styles.textInput, textAlign: "center" }}
                    type="number" placeholder="0" min="0"
                    value={form.femaleCount}
                    onChange={(e) => update("femaleCount", e.target.value)}
                  />
                </div>
              </div>
              {totalMembers > 0 && (
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 6 }}>총 {totalMembers}명</div>
              )}
            </FieldGroup>

            {/* 주요 연령대 */}
            <FieldGroup label="주요 연령대" required error={errors.ages} hint="중복 선택 가능">
              <div style={styles.ageGrid}>
                {AGE_GROUPS.map((age) => (
                  <button key={age}
                    style={{ ...styles.ageBtn, borderColor: form.ages.includes(age) ? "#E24B4A" : "#e5e7eb", background: form.ages.includes(age) ? "#fff5f5" : "#f5f6fa", color: form.ages.includes(age) ? "#E24B4A" : "#6b7280" }}
                    onClick={() => toggleAge(age)}
                  >{age}</button>
                ))}
              </div>
            </FieldGroup>

            {/* 평균 구력 */}
            <FieldGroup label="구성원 평균 구력">
              <div style={styles.careerBox}>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#E24B4A", textAlign: "center" }}>{careerInfo.label}</div>
                <div style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", marginBottom: 12 }}>{careerInfo.desc}</div>
                <input type="range" min={0} max={7} step={1} value={form.career}
                  onChange={(e) => update("career", Number(e.target.value))}
                  style={styles.slider}
                />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#9ca3af", marginTop: 4 }}>
                  <span>입문</span><span>3년</span><span>7년</span><span>10년+</span>
                </div>
              </div>
            </FieldGroup>

            {/* 매칭 가능 요일 */}
            <FieldGroup label="매칭 가능 요일" hint="중복 선택 가능">
              <div style={{ display: "flex", gap: 7 }}>
                {DAYS_OF_WEEK.map((day) => (
                  <button key={day}
                    style={{ ...styles.dayBtn, borderColor: form.availableDays.includes(day) ? "#E24B4A" : "#e5e7eb", background: form.availableDays.includes(day) ? "#E24B4A" : "#f5f6fa", color: form.availableDays.includes(day) ? "#fff" : "#6b7280" }}
                    onClick={() => toggleDay(day)}
                  >{day}</button>
                ))}
              </div>
            </FieldGroup>

            {/* 매칭 가능 시간 */}
            <FieldGroup label="매칭 가능 시간" hint="중복 선택 가능">
              <div style={styles.chipWrap}>
                {TIMES_OF_DAY.map((time) => (
                  <button key={time}
                    style={{ ...styles.chip, borderColor: form.availableTimes.includes(time) ? "#E24B4A" : "#e5e7eb", background: form.availableTimes.includes(time) ? "#E24B4A" : "#f5f6fa", color: form.availableTimes.includes(time) ? "#fff" : "#6b7280" }}
                    onClick={() => toggleTime(time)}
                  >{time}</button>
                ))}
              </div>
            </FieldGroup>

            {/* 주차 가능 여부 */}
            <FieldGroup label="주차 가능 여부">
              <div style={{ display: "flex", gap: 10 }}>
                {[{ v: true, label: "✅ 가능" }, { v: false, label: "❌ 불가능" }].map(({ v, label }) => (
                  <button key={label}
                    style={{ ...styles.chip, flex: 1, borderColor: form.parking === v ? "#E24B4A" : "#e5e7eb", background: form.parking === v ? "#E24B4A" : "#f5f6fa", color: form.parking === v ? "#fff" : "#6b7280" }}
                    onClick={() => update("parking", v)}
                  >{label}</button>
                ))}
              </div>
            </FieldGroup>

            {/* 주차 가능 대수 (주차 가능 시에만) */}
            {form.parking === true && (
              <FieldGroup label="주차 가능 대수">
                <input
                  style={styles.textInput}
                  type="number" placeholder="예: 10" min="0"
                  value={form.parkingCount}
                  onChange={(e) => update("parkingCount", e.target.value)}
                />
              </FieldGroup>
            )}

            {saveError && (
              <div style={styles.errorBox}>
                <span style={{ fontSize: 14 }}>⚠️</span>
                <span style={{ fontSize: 13, color: "#dc2626", marginLeft: 8 }}>{saveError}</span>
              </div>
            )}
          </>
        )}
      </div>

      <div style={styles.footer}>
        <button style={styles.secondaryBtn} onClick={step === 1 ? onBack : () => setStep(1)} disabled={isLoading || logoCompressing || photoCompressing}>
          {step === 1 ? "취소" : "이전"}
        </button>
        <button
          style={{ ...styles.primaryBtn, flex: 1, opacity: (isLoading || logoCompressing || photoCompressing) ? 0.6 : 1, cursor: (isLoading || logoCompressing || photoCompressing) ? "not-allowed" : "pointer" }}
          onClick={handleNext}
          disabled={isLoading || logoCompressing || photoCompressing}
        >
          {logoCompressing || photoCompressing ? "이미지 최적화 중..." : isLoading ? "저장 중..." : step === 1 ? "다음" : isEdit ? "수정 완료" : "등록 완료"}
        </button>
      </div>

      <AddressSearchModal
        open={showAddrModal}
        onClose={() => setShowAddrModal(false)}
        initialQuery={form.mainPlace}
        onSelect={(place) => {
          setForm((p) => ({
            ...p,
            mainPlace: place.name,
            mainPlaceAddress: place.roadAddress || place.address || "",
            mainPlaceMapx: place.mapx,
            mainPlaceMapy: place.mapy,
          }));
        }}
      />
    </div>
  );
}


function FieldGroup({ label, required, error, hint, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{label}</span>
        {required && <span style={{ fontSize: 11, color: "#E24B4A" }}>필수</span>}
        {hint && <span style={{ fontSize: 11, color: "#9ca3af" }}>· {hint}</span>}
      </div>
      {children}
      {error && <p style={{ fontSize: 11, color: "#E24B4A", marginTop: 6 }}>{error}</p>}
    </div>
  );
}

function SummaryRow({ icon, label, value, last }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: last ? "none" : "1px solid #f0f1f5" }}>
      <span style={{ fontSize: 13, color: "#9ca3af" }}>{icon} {label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{value}</span>
    </div>
  );
}

const styles = {
  container:       { minHeight: "100vh", background: "#f5f6fa", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", fontFamily: "'Noto Sans KR', sans-serif" },
  header:          { padding: "12px 20px", borderBottom: "1px solid #e5e7eb", background: "#ffffff", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" },
  headerTop:       { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 },
  backBtn:         { display: "flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 },
  logoRow:         { display: "flex", alignItems: "center", gap: 8 },
  logoText:        { fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" },
  logoSub:         { fontSize: 12, color: "#9ca3af", fontWeight: 400 },
  headerTitleRow:  { display: "flex", alignItems: "center", justifyContent: "space-between" },
  headerTitle:     { fontSize: 16, fontWeight: 700, color: "#111827" },
  stepRow:         { display: "flex", alignItems: "center", gap: 8 },
  stepDot:         { width: 22, height: 22, borderRadius: "50%", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" },
  stepLine:        { width: 32, height: 1, background: "#e5e7eb", margin: "0 4px" },
  body:            { flex: 1, padding: "24px 20px", overflowY: "auto" },
  textInput:       { width: "100%", padding: "12px 14px", background: "#f5f6fa", border: "1px solid #e5e7eb", borderRadius: 10, color: "#111827", fontSize: 15, outline: "none", boxSizing: "border-box" },
  textarea:        { width: "100%", padding: "12px 14px", background: "#f5f6fa", border: "1px solid #e5e7eb", borderRadius: 10, color: "#111827", fontSize: 14, outline: "none", boxSizing: "border-box", resize: "none", fontFamily: "inherit", lineHeight: 1.6 },
  sportGrid:       { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 },
  sportBtn:        { display: "flex", flexDirection: "column", alignItems: "center", padding: "12px 4px", border: "1px solid #e5e7eb", borderRadius: 10, background: "#f5f6fa", cursor: "pointer", transition: "all 0.15s" },
  chipWrap:        { display: "flex", flexWrap: "wrap", gap: 8 },
  chip:            { padding: "7px 14px", fontSize: 13, fontWeight: 600, border: "1px solid #e5e7eb", borderRadius: 999, background: "#f5f6fa", color: "#6b7280", cursor: "pointer", transition: "all 0.15s" },
  dayBtn:          { width: 38, height: 38, fontSize: 13, fontWeight: 700, border: "1px solid #e5e7eb", borderRadius: "50%", background: "#f5f6fa", cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 },
  genderLabel:     { fontSize: 12, color: "#9ca3af", marginBottom: 6, textAlign: "center" },
  ageGrid:         { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 },
  ageBtn:          { padding: "10px 4px", fontSize: 13, fontWeight: 600, border: "1px solid #e5e7eb", borderRadius: 10, background: "#f5f6fa", cursor: "pointer", transition: "all 0.15s" },
  careerBox:       { padding: "16px", background: "#ffffff", borderRadius: 12, border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" },
  slider:          { width: "100%", accentColor: "#E24B4A" },
  errorBox:        { display: "flex", alignItems: "center", padding: "12px 14px", background: "#fff5f5", borderRadius: 10, border: "1px solid #fecaca", marginTop: 8 },
  footer:          { display: "flex", gap: 10, padding: "12px 20px 90px", borderTop: "1px solid #e5e7eb", background: "#ffffff" },
  primaryBtn:      { padding: "14px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer", transition: "opacity 0.15s" },
  secondaryBtn:    { padding: "14px 20px", background: "#f5f6fa", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: "pointer" },
  logoUploadBox:   { width: 88, height: 88, borderRadius: 12, border: "2px dashed #e5e7eb", background: "#f5f6fa", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, overflow: "hidden" },
  photoUploadBox:  { width: "100%", height: 140, borderRadius: 12, border: "2px dashed #e5e7eb", background: "#f5f6fa", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  photoPreview:    { width: "100%", height: 160, objectFit: "cover", borderRadius: 12, border: "1px solid #e5e7eb" },
  uploadBtn:       { padding: "8px 16px", background: "#111827", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "block", width: "100%" },
  successWrapper:  { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 24px 40px", position: "relative", overflow: "hidden" },
  successBurst:    { position: "absolute", top: -80, left: "50%", transform: "translateX(-50%)", width: 300, height: 300, background: "radial-gradient(circle, rgba(226,75,74,0.10) 0%, transparent 70%)", borderRadius: "50%", pointerEvents: "none" },
  successIconWrap: { width: 72, height: 72, background: "#E24B4A", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 },
  successTitle:    { fontSize: 14, color: "#E24B4A", fontWeight: 700, letterSpacing: 2, marginBottom: 8 },
  successTeam:     { fontSize: 28, fontWeight: 700, color: "#111827", marginBottom: 8, letterSpacing: "-0.5px", textAlign: "center" },
  successSub:      { fontSize: 14, color: "#9ca3af", marginBottom: 32 },
  summaryCard:     { width: "100%", background: "#ffffff", borderRadius: 14, padding: "4px 16px", border: "1px solid #e5e7eb", marginBottom: 32, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" },
};
